import React, { useEffect, useState, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, EffectCoverflow } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

// Swiper‑based lightbox viewer
const SimpleGallery = ({ onClose = () => {}, imgs, initialIndex = 0 , className=""}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const thumbnailContainerRef = useRef(null);
  const thumbnailRefs = useRef([]);
  const swiperRef = useRef(null);

  /* Center active thumbnail */
  useEffect(() => {
    if (thumbnailContainerRef.current && thumbnailRefs.current[currentIndex]) {
      const container = thumbnailContainerRef.current;
      const activeThumb = thumbnailRefs.current[currentIndex];
      const scrollLeft =
        activeThumb.offsetLeft -
        container.offsetWidth / 2 +
        activeThumb.offsetWidth / 2;

      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }
  }, [currentIndex]);

  /* Close on ESC */
  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const goToNextImage = () => swiperRef.current?.slideNext();
  const goToPrevImage = () => swiperRef.current?.slidePrev();
  const goToImage = (idx) => swiperRef.current?.slideTo(idx);

  return (
    <div className={"fixed inset-0 bg-black/95 z-50 flex flex-col "+className}>
      {/* Top controls */}
      <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-4">
        <div className="bg-black/50 text-white px-4 py-2 rounded-full text-sm font-medium">
          {currentIndex + 1} / {imgs.length}
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X size={24} className="text-white" />
        </button>
      </div>

      {/* Navigation arrows */}
      <button
        className="swiper-button-prev-custom absolute left-6 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-lg z-10"
        onClick={goToPrevImage}
      >
        <ChevronLeft size={36} className="text-black" />
      </button>
      <button
        className="swiper-button-next-custom absolute right-6 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-lg z-10"
        onClick={goToNextImage}
      >
        <ChevronRight size={36} className="text-black" />
      </button>

      {/* Main Swiper */}
      <div className="flex-1 flex items-center justify-center p-4 pb-24">
        <Swiper
          className="h-full w-full"
          modules={[Navigation, Keyboard, EffectCoverflow]}
          effect="coverflow"
          coverflowEffect={{
            rotate: 50,
            stretch: 0,
            depth: 100,
            modifier: 1,
            slideShadows: true,
          }}
          centeredSlides
          slidesPerView="auto"
          navigation={{
            nextEl: ".swiper-button-next-custom",
            prevEl: ".swiper-button-prev-custom",
          }}
          keyboard={{ enabled: true }}
          spaceBetween={30}
          initialSlide={initialIndex}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
          loop
        >
          {imgs.map((image, idx) => (
            <SwiperSlide
              key={idx}
              className="relative flex items-center justify-center h-full"
            >
              <img
                src={typeof image === "string" ? image : image.src}
                alt={
                  typeof image === "object" ? image.alt : `Image ${idx + 1}`
                }
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-4xl max-h-full object-contain w-full"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Thumbnails */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm p-4">
        <div
          ref={thumbnailContainerRef}
          className="flex items-center gap-2 p-2 overflow-x-auto max-w-full"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {imgs.map((img, idx) => {
            const active = idx === currentIndex;
            return (
              <button
                key={idx}
                ref={(el) => (thumbnailRefs.current[idx] = el)}
                onClick={() => goToImage(idx)}
                className={`flex-shrink-0 rounded-lg overflow-hidden transition-all duration-200 ${
                  active
                    ? "ring-2 ring-white"
                    : "hover:ring-1 hover:ring-white/50"
                }`}
                style={{
                  width: "60px",
                  height: "45px",
                  opacity: active ? 1 : 0.7,
                }}
              >
                <img
                  src={typeof img === "string" ? img : img.src}
                  alt={
                    typeof img === "object"
                      ? img.alt
                      : `Thumbnail ${idx + 1}`
                  }
                  className="w-full h-full object-cover"
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SimpleGallery;